import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Edit, Trash2, X, Loader2, Layers, MapPin } from 'lucide-react';
import { adminApi, adminErrorMsg } from '../../admin/adminApiClient';
import { toast } from 'sonner';

function CrudTable({ type, endpoint, columns, emptyForm, renderRow, renderForm }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get(endpoint);
      setItems(r.data?.items || r.data || []);
    } catch (e) { toast.error(adminErrorMsg(e)); }
  }, [endpoint]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  const openCreate = () => { setEditId(null); setForm({ ...emptyForm }); setDrawerOpen(true); };
  const openEdit = (item) => { setEditId(item.id); setForm(columns.reduce((a, c) => ({ ...a, [c.key]: item[c.key] || '' }), {})); setDrawerOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await adminApi.patch(`${endpoint}/${editId}`, form);
        toast.success(`${type} updated`);
      } else {
        await adminApi.post(endpoint, form);
        toast.success(`${type} created`);
      }
      setDrawerOpen(false);
      await load();
    } catch (e) { toast.error(adminErrorMsg(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${type.toLowerCase()}?`)) return;
    try { await adminApi.delete(`${endpoint}/${id}`); toast.success('Deleted'); await load(); }
    catch (e) { toast.error(adminErrorMsg(e)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button data-testid={`${type.toLowerCase()}-create-btn`} onClick={openCreate} className="flex items-center gap-1.5 bg-brand-magenta hover:bg-pink-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add {type}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-brand-magenta" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${type.toLowerCase()}-table`}>
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  {columns.map((c) => (
                    <th key={c.key} className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">{c.label}</th>
                  ))}
                  <th className="text-right px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={item.id} data-testid={`${type.toLowerCase()}-row-${item.id}`} className="hover:bg-gray-50">
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-3 text-gray-800">
                        {c.render ? c.render(item[c.key], item) : String(item[c.key] ?? '—')}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right flex items-center justify-end gap-1">
                      <button data-testid={`${type.toLowerCase()}-edit-${item.id}`} onClick={() => openEdit(item)} className="p-1.5 rounded hover:bg-gray-100"><Edit className="w-4 h-4 text-gray-500" /></button>
                      <button data-testid={`${type.toLowerCase()}-delete-${item.id}`} onClick={() => remove(item.id)} className="p-1.5 rounded hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {items.length === 0 && <p className="text-center text-sm text-gray-400 py-10">No {type.toLowerCase()}s found</p>}
        </div>
      )}

      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <div className="relative bg-white rounded-xl w-full max-w-md p-6 mx-4 shadow-xl" data-testid={`${type.toLowerCase()}-form-dialog`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900">{editId ? `Edit ${type}` : `New ${type}`}</h3>
              <button onClick={() => setDrawerOpen(false)} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="flex flex-col gap-3">
              {renderForm(form, setForm)}
              <button data-testid={`${type.toLowerCase()}-save-btn`} type="submit" disabled={saving} className="w-full bg-brand-magenta hover:bg-pink-700 disabled:opacity-60 text-white py-2.5 rounded-lg font-bold text-sm transition-colors mt-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminMastersPage() {
  const [tab, setTab] = useState('categories');

  return (
    <div data-testid="admin-masters-page">
      <h1 className="text-xl font-black text-gray-900 mb-4">Masters</h1>

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-4">
        <button
          data-testid="masters-tab-categories"
          onClick={() => setTab('categories')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-bold transition-colors ${tab === 'categories' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Layers className="w-4 h-4" /> Categories
        </button>
        <button
          data-testid="masters-tab-cities"
          onClick={() => setTab('cities')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-bold transition-colors ${tab === 'cities' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <MapPin className="w-4 h-4" /> Cities
        </button>
      </div>

      {tab === 'categories' ? (
        <CrudTable
          type="Category"
          endpoint="/categories"
          columns={[
            { key: 'name', label: 'Name', render: (v) => <span className="font-semibold">{v}</span> },
            { key: 'slug', label: 'Slug' },
            { key: 'icon', label: 'Icon' },
          ]}
          emptyForm={{ name: '', slug: '', icon: '' }}
          renderForm={(form, setForm) => (
            <>
              <Field label="Name"><input data-testid="category-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" placeholder="Category name" /></Field>
              <Field label="Slug (optional)"><input data-testid="category-slug-input" value={form.slug || ''} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="input-field" placeholder="auto-generated if empty" /></Field>
              <Field label="Icon (optional)"><input data-testid="category-icon-input" value={form.icon || ''} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="input-field" placeholder="e.g. music" /></Field>
            </>
          )}
        />
      ) : (
        <CrudTable
          type="City"
          endpoint="/cities"
          columns={[
            { key: 'name', label: 'Name', render: (v) => <span className="font-semibold">{v}</span> },
            { key: 'state', label: 'State' },
            { key: 'country', label: 'Country' },
            { key: 'is_active', label: 'Active', render: (v) => (
              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${v !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                {v !== false ? 'YES' : 'NO'}
              </span>
            )},
          ]}
          emptyForm={{ name: '', state: '', country: '', is_active: true }}
          renderForm={(form, setForm) => (
            <>
              <Field label="City name"><input data-testid="city-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" placeholder="City name" /></Field>
              <Field label="State"><input data-testid="city-state-input" value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })} className="input-field" placeholder="State" /></Field>
              <Field label="Country"><input data-testid="city-country-input" value={form.country || ''} onChange={(e) => setForm({ ...form, country: e.target.value })} className="input-field" placeholder="Country" /></Field>
              <label className="flex items-center gap-2 cursor-pointer">
                <input data-testid="city-active-input" type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 rounded border-gray-300 text-brand-magenta" />
                <span className="text-sm font-semibold text-gray-700">Active</span>
              </label>
            </>
          )}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-bold text-gray-500">{label}</label>
      {children}
    </div>
  );
}
